import { FC, useEffect, useState } from 'react'
import { Responsive, WidthProvider } from 'react-grid-layout'
import { bfs, generateLayout, getCurrentBreakpoint } from '../utils/helper'
import { v4 as uuidv4 } from 'uuid'
import _ from 'lodash'
import { constants } from '../utils/constants'
import PageHeader from './parts/PageHeader'
import PageSection from './parts/PageSection'
import { MainPanelProps } from '../PageBuilder.d'

const ResponsiveReactGridLayout = WidthProvider(Responsive)

const MainPanel: FC<MainPanelProps> = ({
    toolboxItem,
    isEditing,
    items,
    formikProps,
    availablePages,
    deletedCtas,
    setDeletedCtas,
    headerSizeCodes,
    previewTheme,
}) => {
    // TODO: Implement multi-lingual tabs
    const [layouts, setLayouts] = useState<any>(generateLayout(items))
    const [breakpoint, setBreakpoint] = useState<any>(getCurrentBreakpoint())
    const [nextId, setNextId] = useState<any>(uuidv4())

    useEffect(() => {
        setLayouts(generateLayout(items))
    }, [items])

    let dropping = false

    const resolver = () => {
        return JSON.stringify({
            layouts,
            isEditing,
            breakpoint,
        })
    }

    const memorizedItems = _.memoize(() => {
        return layouts[breakpoint].map(({ i, type, pagePart }) => {
            const partType = type.code || constants.toolsTypes.PAGE_SECTION.code

            return (
                <div key={i} className="border">
                    {(() => {
                        switch (partType) {
                            case constants.toolsTypes.HEADER.code:
                                return (
                                    <PageHeader
                                        key={i}
                                        data={{ i, type, pagePart }}
                                        formikProps={formikProps}
                                        availablePages={availablePages}
                                        deletedCtas={deletedCtas}
                                        setDeletedCtas={setDeletedCtas}
                                        headerSizeCodes={headerSizeCodes}
                                        previewTheme={previewTheme}
                                    />
                                )
                            default:
                                return (
                                    <PageSection
                                        key={i}
                                        data={{ i, type, pagePart }}
                                        formikProps={formikProps}
                                        availablePages={availablePages}
                                    />
                                )
                        }
                    })()}
                </div>
            )
        })
    }, resolver)

    const handleDrop = (layout, item, e) => {
        const { type } = toolboxItem
        const newLayouts = _.cloneDeep(layouts)
        const newItem = {
            ...item,
            type,
            isDraggable: undefined,
            isResizable: undefined,
        }

        Object.keys(newLayouts).map((size) => {
            newLayouts[size] = bfs(newLayouts[size], newItem)
            return true
        })

        setLayouts(newLayouts)
        setNextId(uuidv4())
        dropping = true
    }

    const getDroppingItem = () => {
        if (!toolboxItem) {
            return null
        }

        const type = toolboxItem.type || constants.toolsTypes.PAGE_SECTION

        return {
            ...toolboxItem,
            w: 12,
            h: type.height,
            i: nextId,
        }
    }

    const droppingItem = getDroppingItem()

    const handleLayoutChange = (layout) => {
        if (dropping) {
            return
        }

        if (layout.find(({ i }) => i === nextId)) {
            return
        }

        const newLayouts = _.cloneDeep(layouts)
        Object.keys(newLayouts).map((size) => {
            newLayouts[size] = newLayouts[size].map((item, index) => {
                const original = layouts[size] || layouts.lg
                return { ...original[index], ...item }
            })

            return true
        })

        setLayouts(newLayouts)
    }

    const handleBreakpointChange = (breakpoint) => {
        setBreakpoint(breakpoint)
    }

    return (
        <div style={{ scale: '80%', transformOrigin: 'top' }}>
            <ResponsiveReactGridLayout
                className="samirify-page-builder-preview"
                layouts={layouts}
                isDroppable={true}
                isDraggable={isEditing}
                isResizable={false}
                onDrop={handleDrop}
                droppingItem={droppingItem}
                // preventCollision={true}
                // isBounded={true}
                // measureBeforeMount={false}
                onLayoutChange={handleLayoutChange}
                onBreakpointChange={handleBreakpointChange}
                useCSSTransforms={false}
                breakpoints={{ lg: 1200, md: 992, sm: 768 }}
                cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
            >
                {memorizedItems()}
            </ResponsiveReactGridLayout>
        </div>
    )
}

export default MainPanel
